import { useMutation } from '@apollo/client'
import { Button, Modal, Text } from '@chaine/keychaine'
import { encode } from 'base-64'
import React, { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import config from '../../../_server/config'
import { Toast } from '../../../_shared/components/toast'
import { AppModalProps } from '../components/apps-data'
import { SAVE_CLIENT_DETAILS, SAVE_STATE_PARAM, SaveClientDetailsInput, SaveStateParamInput } from '../data/mutations'

/**
 * Starts an OAuth flow for an app.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const EnableOAuthAppModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, oAuthBaseURL, oAuthRedirectURISuffix, oAuthScope } = props
  const { showToast } = Toast()
  const [clientID, setClientID] = useState('')

  const [saveClientDetails, { loading }] = useMutation<{ saveClientDetails: { id: string } }, SaveClientDetailsInput>(
    SAVE_CLIENT_DETAILS,
    {
      onCompleted: (data) => {
        setClientID(data.saveClientDetails.id)

        const stateParam = uuidv4()
        saveStateParam({ variables: { appType: appType, id: stateParam } })
      },
      onError: (err) => {
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  )

  const [saveStateParam, { loading: saveParamLoading }] = useMutation<
    { saveStateParam: { id: string } },
    SaveStateParamInput
  >(SAVE_STATE_PARAM, {
    onCompleted: (data) => {
      /** We use the base 64 to pass relevant, non-sensitive state information to the backend including the state param and the location of the user */
      const state = {
        href: window.location.href,
        origin: window.location.origin,
        path: window.location.pathname,
        stateParam: data.saveStateParam.id
      }
      const stateJson = JSON.stringify(state)
      const base64State = encode(stateJson)

      const thirdPartyOAuthURL = `${oAuthBaseURL}?client_id=${clientID}&redirect_uri=${
        config.app.BASE_OAUTH_CALLBACK_URL
      }/${oAuthRedirectURISuffix}&state=${base64State}&response_type=code${oAuthScope ? `&scope=${oAuthScope}` : ''}`

      window.location.href = thirdPartyOAuthURL
    },
    onError: (err) => {
      showToast({
        status: 'error',
        title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      })
    }
  })

  const handleSaveDetails = () => {
    saveClientDetails({
      variables: { appType: appType }
    })
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires a ${appType} account.`}
        footer={
          <>
            <Button isLoading={loading || saveParamLoading} variant={'primary'} mr={3} onClick={handleSaveDetails}>
              Enable
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        }
      >
        <Text>
          {`When enabling, you will be prompted to give Chaine access to install the ${appType}-Chaine app by
          authenticating with your ${appType} login, and clicking  "Allow"`}
        </Text>
      </Modal>
    </>
  )
})

EnableOAuthAppModal.displayName = 'EnableOAuthAppModal'

export { EnableOAuthAppModal }
